import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
// Deliberately imports no email helpers: this page is public and must never
// be able to send mail. See the note at the submit handler.
import FusionLogo from '../components/FusionLogo';
import { fmtDate } from '../lib/dates';

// Defined at module scope — if this lived inside SupplierSetupForm, every render
// would produce a new Shell reference, React would treat it as a brand-new
// component, and the entire form (inputs included) would unmount/remount on
// each keystroke, killing input focus.
const Shell = ({ children }) => (
  <div className="min-h-screen bg-page-supplier flex flex-col"
    style={{backgroundImage: 'radial-gradient(ellipse at 65% 0%, rgba(146,212,0,0.05) 0%, transparent 55%)'}}>
    <header className="bg-surface-sidebar border-b border-white/[0.08] px-8 py-5 flex items-center gap-6 flex-wrap">
      {/* Dark admin-chrome lockup — matches the sidebar + supplier portal
          header so the setup form reads as part of the same family. */}
      <FusionLogo variant="light" className="h-16 w-auto object-contain flex-shrink-0" />
      <div>
        <p className="font-['Metropolis'] text-[10px] font-bold tracking-[3px] uppercase text-sb-muted mb-1">Supply Pilot</p>
        <h1 className="font-['Metropolis'] text-2xl font-bold tracking-tight text-sb-text">Supplier Set-Up</h1>
      </div>
    </header>
    <main className="flex-1 px-6 py-10">
      <div className="max-w-3xl mx-auto">{children}</div>
    </main>
    <footer className="border-t border-line px-8 py-4">
      <p className="font-['Metropolis'] text-xs text-[#51534A]">If you weren't expecting this form, please contact the Fusion 2K team member who sent you the link.</p>
    </footer>
  </div>
);

const supplierFields = [
  'supplier_company_legal_name', 'supplier_trading_name', 'supplier_reg_number',
  'supplier_years_trading', 'supplier_vat_number', 'supplier_type',
  'supplier_address_line_1', 'supplier_address_line_2', 'supplier_address_line_3',
  'supplier_town', 'supplier_postcode',
  'supplier_accounts_contact_name', 'supplier_accounts_contact_email', 'supplier_accounts_contact_phone',
  'supplier_bank_name', 'supplier_bank_account_name', 'supplier_bank_sort_code', 'supplier_bank_account_number',
  'supplier_accepts_credit_card',
  'supplier_form_completed_by_name', 'supplier_form_completed_by_position', 'supplier_form_signature',
];

export default function SupplierSetupForm() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [supplier, setSupplier] = useState(null);
  const [linkInvalid, setLinkInvalid] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // tcDocument state removed in Phase 6 — T&C now flows through the
  // unified compliance-acknowledge path post-activation.
  const [agreement, setAgreement] = useState(false);
  const sigPadRef = useRef(null);
  const [form, setForm] = useState({
    supplier_company_legal_name: '', supplier_trading_name: '', supplier_reg_number: '',
    supplier_years_trading: '', supplier_vat_number: '', supplier_type: '',
    supplier_address_line_1: '', supplier_address_line_2: '', supplier_address_line_3: '',
    supplier_town: '', supplier_postcode: '',
    supplier_accounts_contact_name: '', supplier_accounts_contact_email: '', supplier_accounts_contact_phone: '',
    supplier_bank_name: '', supplier_bank_account_name: '', supplier_bank_sort_code: '', supplier_bank_account_number: '',
    supplier_accepts_credit_card: '',
    supplier_form_completed_by_name: '', supplier_form_completed_by_position: '', supplier_form_signature: '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Token-gated server lookup — anon users can't read `suppliers` directly
      // (that would expose every supplier's details via the public key). The
      // serverless endpoint returns only this token's row, whitelisted columns.
      let data = null;
      try {
        const res = await fetch('/api/supplier-form-lookup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (res.ok) {
          const body = await res.json().catch(() => ({}));
          data = body.supplier || null;
        }
      } catch (err) {
        // network error — fall through to the invalid-link state below
      }

      if (cancelled) return;

      if (!data) {
        setLinkInvalid(true);
        setLoading(false);
        return;
      }
      if (data.supplier_form_submitted) {
        setSupplier(data);
        setAlreadySubmitted(true);
        setLoading(false);
        return;
      }

      setSupplier(data);
      setForm(prev => {
        const next = { ...prev };
        for (const f of supplierFields) {
          if (data[f] !== null && data[f] !== undefined) {
            if (f === 'supplier_accepts_credit_card') {
              next[f] = data[f] === true ? 'yes' : data[f] === false ? 'no' : '';
            } else {
              next[f] = data[f];
            }
          }
        }
        if (!next.supplier_form_completed_by_name && data.main_contact_name) {
          next.supplier_form_completed_by_name = data.main_contact_name;
        }
        return next;
      });

      // T&C lookup removed in Phase 6 — T&Cs are now handled as
      // Acknowledge-type compliance documents on /portal/compliance
      // after the supplier is activated. The setup form no longer
      // links to or fetches the T&C document.

      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const validate = () => {
    const required = [
      ['supplier_company_legal_name', 'Company legal name'],
      ['supplier_reg_number', 'Company registration number'],
      ['supplier_years_trading', 'Years trading'],
      ['supplier_type', 'Supplier type'],
      ['supplier_address_line_1', 'Address line 1'],
      ['supplier_town', 'Town'],
      ['supplier_postcode', 'Postcode'],
      ['supplier_accounts_contact_name', 'Accounts contact name'],
      ['supplier_accounts_contact_email', 'Accounts contact email'],
      ['supplier_accounts_contact_phone', 'Accounts contact phone'],
      ['supplier_bank_name', 'Bank name'],
      ['supplier_bank_account_name', 'Name on bank account'],
      ['supplier_bank_sort_code', 'Bank sort code'],
      ['supplier_bank_account_number', 'Bank account number'],
      ['supplier_accepts_credit_card', 'Accept payment by credit card'],
      ['supplier_form_completed_by_name', 'Completed by — name'],
      ['supplier_form_completed_by_position', 'Position in company'],
      ['supplier_form_signature', 'Digital signature'],
    ];
    for (const [field, label] of required) {
      if (!form[field] || String(form[field]).trim() === '') return `${label} is required`;
    }
    if (!agreement) return 'You must tick the authorisation box to submit';
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) { setError(validationError); return; }
    setSubmitting(true);
    setError(null);

    // The actual UPDATE happens server-side. Public anon users don't have RLS
    // permission to write to suppliers (rightly), so we hand the token and the
    // form payload to a Vercel serverless function which uses the service key
    // to validate the token, persist the row, and return the notification list.
    const payload = {
      ...form,
      supplier_accepts_credit_card: form.supplier_accepts_credit_card === 'yes',
    };

    let submitBody;
    try {
      const submitRes = await fetch('/api/supplier-form-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, payload }),
      });
      submitBody = await submitRes.json().catch(() => ({}));
      if (!submitRes.ok) {
        setError(submitBody.error || `Submission failed (HTTP ${submitRes.status})`);
        setSubmitting(false);
        return;
      }
    } catch (err) {
      setError(err?.message || 'Network error — please try again');
      setSubmitting(false);
      return;
    }

    // The "form completed" notification is sent SERVER-SIDE by
    // /api/supplier-form-submit. It used to be sent from here, which meant this
    // page — a public route needing only a form token — was handed the names
    // and email addresses of Fusion's finance team so the browser could mail
    // them. It was also the last unauthenticated caller of /api/send-email, and
    // the reason that endpoint could not be closed. Both fixed 2026-08-14.
    setSubmitted(true);
    setSubmitting(false);
  };

  const inputClass = "w-full bg-white border border-line rounded-xl text-ink px-4 py-2.5 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] transition-colors";
  // Field labels were #51534A (Fusion Mid Grey) which has poor contrast on the
  // dark background. Bumped to #DADCCD (Fusion Grey) for legibility on a
  // supplier-facing form where readability matters most.
  const labelClass = "font-['Metropolis'] text-xs font-semibold text-fusion-mid-grey block mb-1";
  const sectionClass = "font-['Metropolis'] text-[11px] font-semibold tracking-[0.18em] uppercase text-fusion-forest mb-4 mt-8 pb-2 border-b border-line";

  if (loading) return (
    <Shell>
      <p className="font-['Metropolis'] text-[#51534A] tracking-[3px] uppercase text-sm">Loading...</p>
    </Shell>
  );

  if (linkInvalid) return (
    <Shell>
      <div className="bg-white border border-[#E0290A]/40 rounded-xl p-8">
        <p className="font-['Metropolis'] text-[11px] font-semibold tracking-[0.18em] uppercase text-[#E0290A] mb-3">Link not valid</p>
        <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">This set-up form link is no longer valid. The link may have expired, been replaced by a newer one, or been mistyped. Please contact the Fusion 2K team member who sent you the link.</p>
      </div>
    </Shell>
  );

  if (alreadySubmitted) return (
    <Shell>
      <div className="bg-white border border-[#92D400]/30 rounded-xl p-8">
        <p className="font-['Metropolis'] text-[11px] font-semibold tracking-[0.18em] uppercase text-fusion-forest mb-3">Already completed</p>
        <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">Thank you — this form has already been submitted for <span className="text-ink font-bold">{supplier?.supplier_name}</span>. If you need to update any details, please contact the Fusion 2K team member who sent you the link.</p>
      </div>
    </Shell>
  );

  if (submitted) return (
    <Shell>
      <div className="bg-white border border-[#92D400]/30 rounded-xl p-8 text-center">
        <p className="font-['Metropolis'] text-[11px] font-semibold tracking-[0.18em] uppercase text-fusion-forest mb-3">Submitted</p>
        <h2 className="font-['Metropolis'] text-3xl font-bold tracking-tight text-ink-primary mb-3">Thank you</h2>
        <p className="font-['Metropolis'] text-base text-fusion-mid-grey mb-2">Your set-up details for <span className="text-ink font-bold">{supplier.supplier_name}</span> have been received.</p>
        <p className="font-['Metropolis'] text-sm text-[#51534A]">The Fusion finance team will review and complete your account set-up. You can close this window.</p>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <div className="bg-white border border-line rounded-xl p-8 mb-6">
        <p className="font-['Metropolis'] text-xs font-semibold text-fusion-forest mb-1">Welcome</p>
        <h2 className="font-['Metropolis'] text-2xl font-bold tracking-tight text-ink-primary mb-3">{supplier.supplier_name}</h2>
        <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">Please complete the form below to be set up as a supplier of Fusion 2K. Fields marked with * are required. Once submitted, this form cannot be re-opened — please complete it in one sitting where possible.</p>
      </div>

      {error && (
        <div className="bg-[#E0290A]/10 border border-[#E0290A]/30 rounded-xl px-4 py-3 mb-6">
          <p className="font-['Metropolis'] text-sm text-[#E0290A]">{error}</p>
        </div>
      )}

      <p className={sectionClass}>Company Details</p>
      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2">
          <label className={labelClass}>Company Legal Name *</label>
          <input className={inputClass} value={form.supplier_company_legal_name} onChange={e => set('supplier_company_legal_name', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className={labelClass}>Trading Name (if different)</label>
          <input className={inputClass} value={form.supplier_trading_name} onChange={e => set('supplier_trading_name', e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>Company Registration Number *</label>
          <input className={inputClass} value={form.supplier_reg_number} onChange={e => set('supplier_reg_number', e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>Years Trading *</label>
          <input className={inputClass} value={form.supplier_years_trading} onChange={e => set('supplier_years_trading', e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>VAT Number (if registered)</label>
          <input className={inputClass} value={form.supplier_vat_number} onChange={e => set('supplier_vat_number', e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>Supplier Type *</label>
          <select className={inputClass} value={form.supplier_type} onChange={e => set('supplier_type', e.target.value)}>
            <option value="">Select...</option>
            <option value="Goods">Goods</option>
            <option value="Services">Services</option>
            <option value="Consultancy">Consultancy</option>
            <option value="Subcontractor">Subcontractor</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>

      <p className={sectionClass}>Registered Address</p>
      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2">
          <label className={labelClass}>Address Line 1 *</label>
          <input className={inputClass} value={form.supplier_address_line_1} onChange={e => set('supplier_address_line_1', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className={labelClass}>Address Line 2</label>
          <input className={inputClass} value={form.supplier_address_line_2} onChange={e => set('supplier_address_line_2', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className={labelClass}>Address Line 3</label>
          <input className={inputClass} value={form.supplier_address_line_3} onChange={e => set('supplier_address_line_3', e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>Town *</label>
          <input className={inputClass} value={form.supplier_town} onChange={e => set('supplier_town', e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>Postcode *</label>
          <input className={inputClass} value={form.supplier_postcode} onChange={e => set('supplier_postcode', e.target.value)} />
        </div>
      </div>

      <p className={sectionClass}>Accounts Contact</p>
      <p className="font-['Metropolis'] text-xs text-[#51534A] mb-4">The person Fusion 2K's finance team should contact about invoices and payments. May be the same as your main commercial contact, or someone different.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div>
          <label className={labelClass}>Name *</label>
          <input className={inputClass} value={form.supplier_accounts_contact_name} onChange={e => set('supplier_accounts_contact_name', e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>Email *</label>
          <input type="email" className={inputClass} value={form.supplier_accounts_contact_email} onChange={e => set('supplier_accounts_contact_email', e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>Phone *</label>
          <input className={inputClass} value={form.supplier_accounts_contact_phone} onChange={e => set('supplier_accounts_contact_phone', e.target.value)} />
        </div>
      </div>

      <p className={sectionClass}>Bank Details for Payment</p>
      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className={labelClass}>Bank Name *</label>
          <input className={inputClass} value={form.supplier_bank_name} onChange={e => set('supplier_bank_name', e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>Name on Bank Account *</label>
          <input className={inputClass} value={form.supplier_bank_account_name} onChange={e => set('supplier_bank_account_name', e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>Sort Code *</label>
          <input className={inputClass} value={form.supplier_bank_sort_code} onChange={e => set('supplier_bank_sort_code', e.target.value)} placeholder="00-00-00" />
        </div>
        <div>
          <label className={labelClass}>Account Number *</label>
          <input className={inputClass} value={form.supplier_bank_account_number} onChange={e => set('supplier_bank_account_number', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className={labelClass}>Do you accept payment by credit card? *</label>
          <select className={inputClass} value={form.supplier_accepts_credit_card} onChange={e => set('supplier_accepts_credit_card', e.target.value)}>
            <option value="">Select...</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
      </div>

      {/* Terms & Conditions section removed in Phase 6 of the
          acknowledge feature. T&Cs now flow through the unified
          compliance-acknowledge path post-activation — the supplier
          sees them as a regular Acknowledge-type compliance document
          on /portal/compliance with the same read-tick-sign flow as
          every other ack doc. The setup form's agreement checkbox
          below was reworded to drop the T&C clause; that promise is
          now made (and audit-trailed) inside the compliance flow. */}

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className={labelClass}>Completed By — Name *</label>
          <input className={inputClass} value={form.supplier_form_completed_by_name} onChange={e => set('supplier_form_completed_by_name', e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>Position in Company *</label>
          <input className={inputClass} value={form.supplier_form_completed_by_position} onChange={e => set('supplier_form_completed_by_position', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className={labelClass}>Digital Signature * <span className="text-[#51534A] normal-case tracking-normal font-['Metropolis']">— sign below using your finger on a touchscreen or your mouse</span></label>
          {/* Captured as a base64 PNG data URL on every stroke end. Stored in
              the same supplier_form_signature text column the typed name used
              to live in. The admin view renders it as an <img>. */}
          <div className="bg-[#F9FCF1] border border-line rounded-xl relative overflow-hidden" style={{height: 180}}>
            <SignatureCanvas
              ref={sigPadRef}
              penColor="#131313"
              backgroundColor="#F9FCF1"
              canvasProps={{className: 'w-full h-full', style: {touchAction: 'none'}}}
              onEnd={() => {
                const pad = sigPadRef.current;
                if (!pad) return;
                if (pad.isEmpty()) {
                  set('supplier_form_signature', '');
                } else {
                  set('supplier_form_signature', pad.toDataURL('image/png'));
                }
              }}
            />
          </div>
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => { sigPadRef.current?.clear(); set('supplier_form_signature', ''); }}
              className="font-['Metropolis'] text-xs font-semibold text-[#51534A] hover:text-active-green transition-colors"
            >
              Clear signature
            </button>
          </div>
        </div>
        <div className="col-span-2">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={agreement} onChange={e => setAgreement(e.target.checked)}
              className="mt-1 w-4 h-4 accent-active-green flex-shrink-0" />
            <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">
              I confirm I am authorised to provide these details on behalf of <span className="text-ink font-bold">{supplier.supplier_name}</span> and that the information above is accurate. By signing above and ticking this box I am providing my electronic signature. Fusion 2K's Terms & Conditions will be presented for review and electronic acknowledgement on your supplier portal once your account is activated.
            </span>
          </label>
        </div>
        <div>
          <label className={labelClass}>Date</label>
          <p className="font-['Metropolis'] text-sm text-fusion-mid-grey py-2">{fmtDate(new Date())} (auto-stamped on submit)</p>
        </div>
      </div>

      <div className="mt-10 pt-6 border-t border-line flex justify-end">
        <button onClick={handleSubmit} disabled={submitting}
          className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-sm font-semibold px-8 py-3 hover:bg-[#6a9a00] transition-colors disabled:opacity-50">
          {submitting ? 'Submitting…' : 'Submit Set-Up Form'}
        </button>
      </div>
    </Shell>
  );
}
